'use client';

import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SeoFieldsEditor } from '@/components/painel/seo/SeoFieldsEditor';
import { painelButtonPrimaryClass } from '@/components/painel/painel-styles';
import { emptySeoEntryFields, type SeoEntityType, type SeoEntryFields } from '@/lib/seo';
import { fetchSeoEntry, upsertSeoEntry } from '@/services/seo.service';
import { cn } from '@/lib/utils';

type EntitySeoSectionProps = {
  entityType: SeoEntityType;
  entityId?: number | null;
  pageKey?: string | null;
  /** Só carrega quando true (ex.: power já guardado). */
  enabled?: boolean;
  fallbackTitle?: string;
  fallbackDescription?: string;
  previewPath?: string;
  className?: string;
};

/**
 * Secção SEO reutilizável para editores de guia/power/ace/página.
 * Persiste directamente em /admin/seo/entry (não depende do save do formulário pai).
 */
export function EntitySeoSection({
  entityType,
  entityId = null,
  pageKey = null,
  enabled = true,
  fallbackTitle = '',
  fallbackDescription = '',
  previewPath = '/',
  className,
}: EntitySeoSectionProps) {
  const [fields, setFields] = useState<SeoEntryFields | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [fbTitle, setFbTitle] = useState(fallbackTitle);
  const [fbDesc, setFbDesc] = useState(fallbackDescription);

  useEffect(() => {
    setFbTitle(fallbackTitle);
    setFbDesc(fallbackDescription);
  }, [fallbackTitle, fallbackDescription]);

  useEffect(() => {
    if (!enabled) return;
    if (entityType !== 'page' && !entityId) return;
    if (entityType === 'page' && !pageKey) return;

    let cancelled = false;
    setLoading(true);
    void fetchSeoEntry({ entityType, entityId, pageKey })
      .then((data) => {
        if (cancelled) return;
        setFields(data.entry);
        setFbTitle(data.entity.fallbackTitle || fallbackTitle || data.entity.label);
        setFbDesc(data.entity.fallbackDescription || fallbackDescription);
        setDirty(false);
      })
      .catch(() => {
        if (cancelled) return;
        setFields(emptySeoEntryFields(entityType, entityId, pageKey));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, entityType, entityId, pageKey, fallbackTitle, fallbackDescription]);

  const save = async () => {
    if (!fields) return;
    setSaving(true);
    try {
      const data = await upsertSeoEntry({
        entityType,
        entityId,
        pageKey,
        title: fields.title,
        description: fields.description,
        canonicalUrl: fields.canonicalUrl,
        slugOverride: fields.slugOverride,
        ogTitle: fields.ogTitle,
        ogDescription: fields.ogDescription,
        ogImage: fields.ogImage,
        twitterTitle: fields.twitterTitle,
        twitterDescription: fields.twitterDescription,
        twitterImage: fields.twitterImage,
        twitterCard: fields.twitterCard,
        shareImage: fields.shareImage,
        robotsIndex: fields.robotsIndex,
        robotsFollow: fields.robotsFollow,
        schemaJson: fields.schemaJson,
      });
      setFields(data.entry);
      setDirty(false);
      toast.success('SEO guardado.');
    } catch {
      toast.error('Não foi possível guardar o SEO.');
    } finally {
      setSaving(false);
    }
  };

  if (!enabled) {
    return (
      <p className="font-inter text-sm text-slate-500">
        Guarde o registo primeiro para configurar o SEO individual.
      </p>
    );
  }

  if (loading || !fields) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-sky-300" />
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex justify-end">
        <Button
          type="button"
          disabled={!dirty || saving}
          className={cn(painelButtonPrimaryClass, 'h-9 gap-2')}
          onClick={() => void save()}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar SEO
        </Button>
      </div>
      <SeoFieldsEditor
        value={fields}
        onChange={(next) => {
          setFields(next);
          setDirty(true);
        }}
        fallbackTitle={fbTitle}
        fallbackDescription={fbDesc}
        previewUrl={`https://ajuda.xat.com${previewPath.startsWith('/') ? previewPath : `/${previewPath}`}`}
      />
    </div>
  );
}
